import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import { Button } from './ui/Button';
import { PageLoader } from './ui/PageLoader';
import { AdminQueueState } from './ui/AdminQueueState';
import { AdminDeleteButton } from './ui/AdminDeleteButton';
import { useToast } from '../contexts/ToastContext';

const STATUS_OPTIONS = ['pending_review', 'unverified', 'verified', 'suspended', 'draft'] as const;

const AdminClubsReview: React.FC = () => {
  const { showToast } = useToast();
  const [status, setStatus] = useState<string>('pending_review');
  const [clubs, setClubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async (s = status) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.adminListClubs(s);
      setClubs((res as any).data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load clubs');
      setClubs([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const statusLabel = status.replace(/_/g, ' ');

  const review = async (clubId: number, action: 'approve' | 'verify' | 'suspend' | 'reject') => {
    setBusyId(clubId);
    try {
      await apiService.adminReviewClub(clubId, action);
      showToast(`Club ${action === 'approve' ? 'approved' : action === 'verify' ? 'verified' : action === 'suspend' ? 'suspended' : 'rejected'}.`, 'success');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : `Failed to ${action} club`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="text-white">
      <PageHeader
        eyebrow="Admin"
        title="Club review"
        description="Review and verify club submissions."
        backLink={{ to: '/admin', label: 'Overview' }}
      />
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_OPTIONS.map((s) => (
          <button key={s} type="button" className={status === s ? 'filter-chip-active' : 'filter-chip'} onClick={() => setStatus(s)}>
            {s.replace('_', ' ')}
          </button>
        ))}
      </div>
      {loading ? <PageLoader message="Loading clubs" /> : null}
      {!loading && error ? (
        <AdminQueueState
          variant="error"
          title="Could not load club queue"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}
      {!loading && !error && clubs.length === 0 ? (
        <AdminQueueState
          variant={status === 'pending_review' ? 'empty' : 'filter-empty'}
          title={status === 'pending_review' ? 'No clubs waiting for review' : `No clubs with status “${statusLabel}”`}
          description={
            status === 'pending_review'
              ? 'New club submissions will appear here for approval.'
              : 'Try another status filter to browse the directory.'
          }
          onClearFilter={status !== 'pending_review' ? () => setStatus('pending_review') : undefined}
          clearFilterLabel="Show pending review"
        />
      ) : null}
      {!loading && !error && clubs.length > 0 ? (
        <ul className="space-y-3">
          {clubs.map((c) => (
            <li key={c.id} className="rounded-xl border border-white/[0.06] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <Link to={`/clubs/${c.slug}`} className="text-orange-400 hover:text-orange-300 font-medium">
                  {c.name}
                </Link>
                <p className="text-sm text-gray-500 mt-1">
                  {c.region} · {c.verification_status}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" disabled={busyId === c.id} onClick={() => void review(c.id, 'approve')}>
                  Approve
                </Button>
                <Button variant="primary" disabled={busyId === c.id} onClick={() => void review(c.id, 'verify')}>
                  Verify
                </Button>
                <Button variant="ghost" disabled={busyId === c.id} onClick={() => void review(c.id, 'suspend')}>
                  Suspend
                </Button>
                <Button variant="danger" disabled={busyId === c.id} onClick={() => void review(c.id, 'reject')}>
                  Reject
                </Button>
                <AdminDeleteButton
                  entity="club"
                  id={c.id}
                  confirmToken={c.slug}
                  label={c.name}
                  description={
                    <>
                      Permanently deletes club <strong className="text-white">{c.name}</strong>, memberships,
                      vehicle links, posts, and club badges. Member accounts, vehicles, and linked events stay.
                    </>
                  }
                  onDeleted={() => void load()}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default AdminClubsReview;
